import { TFunction } from 'i18next';
import { BookIcon, CirclePlayIcon, ImageIcon, MapPinIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { ReadOnlyField } from '@/components/atoms/ReadOnlyField/ReadOnlyField';
import TitleSubtitle from '@/components/atoms/TitleSubtitle/TitleSubtitle';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import {
  getComponentsFromTemplate,
  getVariablesLength,
  isMediaHeader,
} from '@/modules/whatsapp/utils';
import { WhatsappTemplate } from '@/shared/types/broadcast';

import { HeaderMediaSection } from './VariablesSection/HeaderMediaSection';
import { VariablesSection } from './VariablesSection/VariablesSection';

import styles from './TemplateBody.module.scss';

interface Props {
  template: WhatsappTemplate;
  isReadOnly: boolean;
}
const getHeader = (header, t: TFunction) => {
  if (!header) {
    return null;
  }
  if (header?.text) {
    return (
      <ReadOnlyField label={t('broadcasts.template_header')} value={header.text} className="mb-4" />
    );
  }
  let icon = null;
  let label = '';
  switch (header?.format?.toLowerCase()) {
    case 'location':
      icon = <MapPinIcon size={16} className="text-foreground-muted" />;
      label = 'Location';
      break;
    case 'document':
      icon = <BookIcon size={16} className="text-foreground-muted" />;
      label = 'document.pdf';
      break;
    case 'image':
      icon = <ImageIcon size={16} className="text-foreground-muted" />;
      label = 'image.jpg';
      break;
    case 'video':
      icon = <CirclePlayIcon size={16} className="text-foreground-muted" />;
      label = 'video.mp4';
      break;
  }
  return (
    <div className="mt-2 mb-4">
      <label className={cn('text-sm leading-5 font-medium', styles.label)}>
        {t('broadcasts.template_header')}
      </label>
      <div className="mt-2 flex items-center gap-1">
        {icon}
        <span className="text-base leading-normal font-normal">{label}</span>
      </div>
    </div>
  );
};
export const TemplateBody = ({ template, isReadOnly }: Props) => {
  const { t } = useTranslation();
  const { header, body, footer, buttons } = getComponentsFromTemplate(template);
  const headerVariablesCount = getVariablesLength(header);
  const bodyVariablesCount = getVariablesLength(body);

  return (
    <>
      <div className="mt-4">
        <TitleSubtitle
          title={t('webhook.test.preview')}
          subtitle={t('broadcasts.template_preview')}
          size="large"
        />
      </div>
      {getHeader(header, t)}

      {headerVariablesCount > 0 && !isMediaHeader(header) && (
        <VariablesSection name="header" isReadOnly={isReadOnly} template={template} />
      )}
      {isMediaHeader(header) && (
        <HeaderMediaSection isReadOnly={isReadOnly} templateHeader={header} />
      )}
      {body?.text && (
        <div className={styles.textarea}>
          <label className={cn('text-sm leading-5 font-medium', styles.label)}>
            {t('dialog.email.body')}
          </label>

          <Textarea
            autoSize
            key={body.text}
            readOnly
            defaultValue={body.text}
            className="w-full resize-none rounded-md border border-stroke bg-bg-muted-50 px-4 py-2 text-sm text-foreground-muted outline-none"
          />
        </div>
      )}
      {bodyVariablesCount > 0 && (
        <VariablesSection name="body" isReadOnly={isReadOnly} template={template} />
      )}

      {footer?.text && (
        <ReadOnlyField label={t('broadcasts.footer')} value={footer.text} className="my-4" />
      )}
      {buttons && (
        <div>
          <label className={cn('text-sm leading-5 font-medium', styles.label)}>
            {t('dialog.carousel.buttons')}
          </label>
          <div className={styles.buttonsContainer}>
            {buttons.map((button) => (
              <ReadOnlyField key={button.text} value={button.text} className="w-[292px]" />
            ))}
          </div>
        </div>
      )}
    </>
  );
};
